import { HttpContext, HttpContextToken } from "@angular/common/http";

/**
 * Marks a request the user did not initiate — a timer poll or a tab-refocus
 * reload. authInterceptor never refreshes the session for one: on a 401 (or
 * with no access token to send) it hands the error straight to the caller,
 * without starting or joining the refresh flow and without opening the
 * session-expired / authentication-failed dialog.
 *
 * Why: the server slides the whole session on every `/auth/token/refresh`
 * (src/servers/routes/auth.routes.ts, "Stage 5"). If background traffic could
 * refresh, a visible but unattended tab would never reach the idle timeout.
 * Callers of a background request must treat any failure as "no update this
 * round" and keep what they have; the next request the user makes refreshes
 * the session as usual.
 *
 * After a background request is answered 401, further background requests
 * with that same access token are not sent at all — they fail locally as a
 * 401 — until the tab holds a new token, so an unattended tab does not hit the
 * API with a dead token on every poll.
 *
 * Lives here (not in auth.interceptor.ts) so a service can mark requests
 * without importing the interceptor and, through it, AuthService.
 *
 * No caller marks a request today. The feature-flag polls did until
 * 2026-10-05: left on an expired access token they sat on a 401 and the
 * flags stopped following, so they go out as ordinary requests now, at the
 * known price that a visible tab keeps its session alive. Mark a poll only
 * where "no update until the user's next click" is acceptable.
 */
export const BACKGROUND_REQUEST = new HttpContextToken<boolean>(() => false);

/** `HttpContext` carrying BACKGROUND_REQUEST, for `http.get(url, { context })`. */
export function backgroundRequestContext(): HttpContext {
  return new HttpContext().set(BACKGROUND_REQUEST, true);
}

/**
 * Tells authInterceptor not to show its own generic error toast (today: the
 * 403 "You do not have permission…" toast) for this request, because the
 * caller reports the failure itself — one toast, with the right cause.
 * The refresh / session-expired handling is unaffected.
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * The statusText of the 409 authInterceptor answers instead of replaying a
 * write under another role than the one it was made under. Nothing was
 * written.
 */
export const ROLE_SWITCHED = "Role switched";

/** `HttpContext` carrying SKIP_ERROR_TOAST. */
export function skipErrorToastContext(): HttpContext {
  return new HttpContext().set(SKIP_ERROR_TOAST, true);
}
